import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { FlagSource, PlayerFlagRow } from "@/lib/api";
import { formatTimestamp } from "@/lib/utils";

export const SOURCE_LABEL: Record<FlagSource, string> = {
  save_check: "Save check", player_report: "Player report", iap_duplicate: "Duplicate purchase", leaderboard_submit: "Leaderboard submission", full_scan: "Full scan",
};

// Presentation only: keep stored reasons intact for deduplication and review.
function readableReason(reason: string): string {
  const match = /^(\S+) history is abnormal \[R:([^\]]+)\] - \[A:([^\]]+)\]$/.exec(reason);
  if (!match) return reason;
  return `${match[1]}: recorded removals ${match[2]}; starting amount + recorded additions ${match[3]} (history mismatch)`;
}

export function parseReasons(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string").map(readableReason) : [];
  } catch { return value ? [readableReason(value)] : []; }
}

function prettyPayload(value: string | null): string {
  if (!value) return "—";
  try { return JSON.stringify(JSON.parse(value), null, 2); }
  catch { return value; }
}

function FlagPayload({ payload }: { payload: string | null }) {
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">("idle");
  const text = prettyPayload(payload);

  async function copyPayload() {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("failed");
    }
  }

  return <div>
    <div className="mb-1 flex items-center gap-2">
      <span className="text-muted-foreground">Latest payload</span>
      <Button type="button" variant="secondary" size="sm" className="h-6 px-2 text-xs" onClick={() => void copyPayload()} disabled={!payload}>
        {copyStatus === "copied" ? "Copied" : "Copy payload"}
      </Button>
      {copyStatus === "failed" && <span role="alert" className="text-destructive">Copy failed</span>}
    </div>
    <pre className="max-h-64 overflow-auto rounded border border-border bg-muted/20 p-2 font-mono text-[11px] whitespace-pre-wrap break-all">{text}</pre>
  </div>;
}

export function FlagDetails({ rows }: { rows: PlayerFlagRow[] }) {
  return <div className="space-y-3">
    <p className="text-muted-foreground">Newest {rows.length} distinct finding{rows.length === 1 ? "" : "s"}. Repeated identical source and reasons are counted together.</p>
    {rows.length === 0 && <p className="text-muted-foreground">No detailed findings retained.</p>}
    {rows.map((row) => { const reasons = parseReasons(row.reasons); return <div key={row.id} className="rounded border border-border bg-background p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={row.source === "player_report" ? "outline" : "secondary"} className="text-[10px]">{SOURCE_LABEL[row.source] ?? row.source}</Badge>
        <span className="text-muted-foreground">Last seen {formatTimestamp(row.ts)}</span>
        <span className="text-muted-foreground">{row.occurrence_count} occurrence{row.occurrence_count === 1 ? "" : "s"}</span>
        {row.reporter_id && <span className="text-muted-foreground">Reported by <Link to={`/players/${encodeURIComponent(row.reporter_id)}`} className="text-blue-300 hover:underline">{row.reporter_id}</Link></span>}
      </div>
      <div><div className="text-muted-foreground mb-1">Reasons</div>{reasons.length ? <ul className="list-disc list-inside space-y-0.5">{reasons.map((reason, i) => <li key={i}>{reason}</li>)}</ul> : <span>—</span>}</div>
      <FlagPayload payload={row.payload} />
    </div>; })}
  </div>;
}
